import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FaqCategorySection } from '../components/FaqCategorySection';
import { FAQ_ARTICLES, FAQ_CATEGORIES } from '../data/faqCatalog';
import styles from './FaqScreen.module.css';

interface FaqScreenProps {
  /** Внутренний deep-link (AC-7). Внешних ссылок нет и не планируется (F-8, F-9). */
  articleId?: string;
}

// PARA-133 (AC-2, AC-3, AC-7, AC-E2, AD-8) — экран справки.
//
// Фича полностью офлайновая (AD-9): ни одного сетевого запроса, ни одного
// обращения к @shared/api-client. Тексты приходят из бандла i18n, изображения —
// из ассетов бандла, так что справка читается и без сети (AC-E5).
export function FaqScreen({ articleId }: FaqScreenProps): JSX.Element {
  const { t } = useTranslation('faq');

  // AD-2: deep-link применяется РОВНО ОДИН раз — при монтировании. Ленивый
  // инициализатор useState фиксирует значение на весь срок жизни экрана, чтобы
  // последующие ре-рендеры (смена локали, раскрытие статей) его не переигрывали.
  // Неизвестный articleId деградирует молча: экран открывается обычным видом,
  // без ошибки и без попытки автопрокрутки (AC-E2, EC-7).
  const [deepLinkArticleId] = useState<string | null>(() =>
    articleId !== undefined && FAQ_ARTICLES.some((article) => article.id === articleId)
      ? articleId
      : null,
  );

  // AD-8: раскрытых статей может быть НЕСКОЛЬКО — режим «раскрыта ровно одна»
  // схлопывал бы уже прочитанное.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(deepLinkArticleId === null ? [] : [deepLinkArticleId]),
  );

  const deepLinkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (deepLinkArticleId === null) {
      return;
    }
    deepLinkRef.current?.scrollIntoView();
  }, [deepLinkArticleId]);

  const handleToggle = useCallback((toggledId: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(toggledId)) {
        next.add(toggledId);
      }
      return next;
    });
  }, []);

  return (
    <div className={styles.screen}>
      <h1 className={styles.title}>{t('title')}</h1>
      {FAQ_CATEGORIES.map((categoryId) => (
        <FaqCategorySection
          key={categoryId}
          categoryId={categoryId}
          expandedIds={expandedIds}
          onToggle={handleToggle}
          deepLinkArticleId={deepLinkArticleId}
          deepLinkRef={deepLinkRef}
        />
      ))}
    </div>
  );
}
